import { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/esm/ExpandMore';
import qsService, { ClaimableCertifiedBqLine } from '../../services/api/qsService';
import useQsFieldData from './useQsFieldData';
import type { Task } from '../../services/api/tasksService';
import type { TaskMaterial } from '../../services/api/taskMaterialsService';

type QsFieldView = 'home' | 'drawdown' | 'previous-drawdowns';

interface QsFieldModePageProps {
  view?: QsFieldView;
}

const money = (value: number) =>
  new Intl.NumberFormat('en-JM', { style: 'currency', currency: 'JMD', maximumFractionDigits: 0 }).format(value);

const percent = (value: number) => `${Math.round(value)}%`;

const lineLocation = (line: ClaimableCertifiedBqLine) =>
  [line.elementGroup, line.subElement].filter(Boolean).join(' - ') || '—';

const packetStatusLabel = (status: string) => status.replace(/_/g, ' ');

const readableDate = (value?: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
};

const monthLabel = (value?: string | null) => {
  if (!value) return 'No date recorded';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString([], { month: 'long', year: 'numeric' });
};

const approvalDate = (task: Task) => task.completionDate ?? task.updatedAt ?? task.dueDate ?? task.createdAt;

const materialLabel = (material: TaskMaterial) => [
  material.resource?.resourceCode,
  material.resource?.name ?? material.resourceId,
].filter(Boolean).join(' - ');

const materialQuantity = (material: TaskMaterial) =>
  `${Number(material.quantityRequired || 0)} ${material.resource?.unitOfMeasure || ''}`.trim();

export default function QsFieldModePage({ view = 'home' }: QsFieldModePageProps) {
  const {
    projectId,
    lines,
    approvedTasks,
    approvedTaskMaterials,
    approvedTasksFailed,
    packets,
    packetsFailed,
    loading,
    error,
    reload,
  } = useQsFieldData();
  const [selectedLineId, setSelectedLineId] = useState<string | null>(null);
  const [approvedDrafts, setApprovedDrafts] = useState<Record<string, string>>({});
  const [certifiedIds, setCertifiedIds] = useState<Set<string>>(new Set());
  const [certifyingId, setCertifyingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  const selectedLine = useMemo(
    () => lines.find((line) => line.id === selectedLineId) ?? lines[0] ?? null,
    [lines, selectedLineId],
  );

  const claimableValue = lines.reduce((sum, line) => sum + line.thisCertificateValue, 0);
  const maximumClaimValue = lines.reduce((sum, line) => sum + line.maximumThisCertificateValue, 0);
  const drawdownProgress = maximumClaimValue > 0 ? Math.round((claimableValue / maximumClaimValue) * 100) : 0;
  const completedItems = lines.filter((line) => line.weightedProgressPercent >= 100).length;
  const partialItems = lines.filter((line) => line.weightedProgressPercent > 0 && line.weightedProgressPercent < 100).length;
  const notStartedItems = lines.filter((line) => line.weightedProgressPercent <= 0).length;
  const approvedTasksByMonth = useMemo(() => {
    const groups = new Map<string, Task[]>();
    [...approvedTasks]
      .sort((a, b) => new Date(approvalDate(b) || 0).getTime() - new Date(approvalDate(a) || 0).getTime())
      .forEach((task) => {
        const key = monthLabel(approvalDate(task));
        groups.set(key, [...(groups.get(key) ?? []), task]);
      });
    return [...groups.entries()];
  }, [approvedTasks]);
  const approvedDrawdownPackets = useMemo(
    () => packets.filter((packet) => ['approved', 'paid'].includes(packet.status)),
    [packets],
  );
  const receivedPurchaseOrderMaterials = useMemo(
    () => lines.flatMap((line) =>
      (line.materials ?? []).map((material) => ({
        ...material,
        bqCode: line.bqCode,
        bqDescription: line.description,
      }))
    ),
    [lines],
  );
  const approvedDrawdownsByMonth = useMemo(() => {
    const groups = new Map<string, typeof approvedDrawdownPackets>();
    [...approvedDrawdownPackets]
      .sort((a, b) =>
        new Date((b.approvedAt ?? b.paidAt ?? b.updatedAt ?? b.createdAt) || 0).getTime()
        - new Date((a.approvedAt ?? a.paidAt ?? a.updatedAt ?? a.createdAt) || 0).getTime()
      )
      .forEach((packet) => {
        const key = monthLabel(packet.approvedAt ?? packet.paidAt ?? packet.updatedAt ?? packet.createdAt);
        groups.set(key, [...(groups.get(key) ?? []), packet]);
      });
    return [...groups.entries()];
  }, [approvedDrawdownPackets]);

  const approvedValueFor = (line: ClaimableCertifiedBqLine) =>
    approvedDrafts[line.id] ?? String(Math.round(line.thisCertificateValue));

  const certifyLine = async (line: ClaimableCertifiedBqLine) => {
    const value = Number(approvedValueFor(line));
    setActionError(null);
    setActionSuccess(null);
    if (!Number.isFinite(value) || value <= 0) {
      setActionError('Approved amount must be a positive number.');
      return;
    }
    if (value > line.maximumThisCertificateValue) {
      setActionError(`Approved amount exceeds the maximum claimable ${money(line.maximumThisCertificateValue)} for ${line.bqCode}.`);
      return;
    }
    try {
      setCertifyingId(line.id);
      const payload = Math.round(value) === Math.round(line.thisCertificateValue)
        ? { percentCertified: line.proposedPercentCertified, certifiedValue: value }
        : { certifiedValue: value };
      await qsService.certifyClaimableCertifiedBqLine(line.id, payload);
      setCertifiedIds((current) => new Set(current).add(line.id));
      setApprovedDrafts((current) => {
        const next = { ...current };
        delete next[line.id];
        return next;
      });
      setActionSuccess(`${line.bqCode} certified at ${money(value)}.`);
      reload();
    } catch (err: any) {
      setActionError(err?.response?.data?.message || err?.response?.data?.error || `Failed to certify ${line.bqCode}`);
    } finally {
      setCertifyingId(null);
    }
  };

  const metric = (label: string, value: string, note?: string) => (
    <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1, minHeight: 96 }}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>{label}</Typography>
      <Typography variant="h6" fontWeight={900} sx={{ mt: 0.5, lineHeight: 1.15 }}>{value}</Typography>
      {note && <Typography variant="caption" color="text.secondary">{note}</Typography>}
    </Paper>
  );

  if (!projectId) {
    return <Alert severity="warning">Select an active project to load QS drawdown data.</Alert>;
  }

  const statusAlerts = (
    <>
      {loading && <LinearProgress sx={{ borderRadius: 1 }} />}
      {error && (
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={reload}>Retry</Button>}>
          {error}
        </Alert>
      )}
      {actionError && <Alert severity="error" onClose={() => setActionError(null)}>{actionError}</Alert>}
      {actionSuccess && <Alert severity="success" onClose={() => setActionSuccess(null)}>{actionSuccess}</Alert>}
    </>
  );

  if (view === 'drawdown') {
    return (
      <Stack spacing={1.5}>
        <Typography variant="h4" fontWeight={900}>QS</Typography>
        <Typography variant="h6" fontWeight={900}>Current Drawdown Request</Typography>
        {statusAlerts}
        {!loading && !error && lines.length === 0 && (
          <Alert severity="info">No certified BQ lines are claimable for this project yet.</Alert>
        )}

        {lines.length > 0 && (
          <>
            <Paper sx={{ p: 1.5, borderRadius: 1 }}>
              <Typography fontWeight={900} sx={{ mb: 1 }}>Official BQ Items Certified For Claim</Typography>
              <Table size="small" sx={{ '& td, & th': { py: 0.45, px: 0.75, fontSize: 12 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell>BQ item</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell align="right">Claim</TableCell>
                    <TableCell align="right">Approved</TableCell>
                    <TableCell align="center">OK</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lines.map((line) => {
                    const blocked = line.readiness?.status === 'blocked';
                    const certified = certifiedIds.has(line.id);
                    return (
                      <TableRow
                        key={line.id}
                        hover
                        selected={line.id === selectedLine?.id}
                        onClick={() => setSelectedLineId(line.id)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>{line.bqCode} {line.description}</TableCell>
                        <TableCell>{lineLocation(line)}</TableCell>
                        <TableCell align="right">{money(line.thisCertificateValue)}</TableCell>
                        <TableCell align="right">
                          <TextField
                            size="small"
                            type="number"
                            value={approvedValueFor(line)}
                            disabled={blocked || certified || certifyingId === line.id}
                            onClick={(event) => event.stopPropagation()}
                            onChange={(event) =>
                              setApprovedDrafts((current) => ({ ...current, [line.id]: event.target.value }))
                            }
                            inputProps={{ style: { padding: 4, fontSize: 12 } }}
                            sx={{ width: 92 }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Box
                            component="button"
                            type="button"
                            title={blocked ? (line.readiness?.blockingReason ?? 'Blocked') : certified ? 'Certified' : 'Certify approved amount'}
                            disabled={blocked || certified || certifyingId === line.id}
                            onClick={(event) => {
                              event.stopPropagation();
                              certifyLine(line);
                            }}
                            sx={{
                              display: 'inline-block',
                              width: 17,
                              height: 17,
                              p: 0,
                              border: '2px solid',
                              borderColor: certified ? 'success.main' : blocked ? 'text.disabled' : 'text.secondary',
                              bgcolor: certified ? 'success.main' : 'transparent',
                              borderRadius: 0.5,
                              cursor: blocked || certified ? 'default' : 'pointer',
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Paper>

            {selectedLine && (
              <Paper sx={{ p: 1.5, borderRadius: 1 }}>
                <Typography fontWeight={900} sx={{ mb: 1 }}>Linked Operational BQ Tasks - Claimed Work Completed</Typography>
                {(selectedLine.mappedTasks || []).length === 0 ? (
                  <Alert severity="info">No operational tasks are mapped to this BQ line.</Alert>
                ) : (
                  <Table size="small" sx={{ '& td, & th': { py: 0.45, px: 0.75, fontSize: 12 } }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Task</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Claimed work</TableCell>
                        <TableCell align="right">Weight</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(selectedLine.mappedTasks || []).map((task) => (
                        <TableRow key={task.id}>
                          <TableCell>{task.taskCode} - {task.name}</TableCell>
                          <TableCell>{task.lifecycleStatus}</TableCell>
                          <TableCell>{percent(task.completionPercentage)}</TableCell>
                          <TableCell align="right">{percent(task.weightPercent)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Paper>
            )}

            {selectedLine && (
              <Paper sx={{ p: 1.5, borderRadius: 1 }}>
                <Typography fontWeight={900} sx={{ mb: 1 }}>Materials Purchased For Claim</Typography>
                {(selectedLine.materials ?? []).length === 0 ? (
                  <Alert severity="info">No claimable materials are recorded for this BQ line.</Alert>
                ) : (
                  <Table size="small" sx={{ '& td, & th': { py: 0.45, px: 0.75, fontSize: 12 } }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Material</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Claim value</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(selectedLine.materials ?? []).map((row) => (
                        <TableRow key={row.id}>
                          <TableCell>{row.description ?? 'Material'}</TableCell>
                          <TableCell><Chip size="small" label={row.status} /></TableCell>
                          <TableCell align="right">{money(row.claimableValue)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Paper>
            )}
          </>
        )}

        <Paper sx={{ p: 1.5, borderRadius: 1 }}>
          <Typography fontWeight={900} sx={{ mb: 1 }}>Supervisor Approved Tasks</Typography>
          {approvedTasksFailed && (
            <Alert severity="warning">Supervisor approved tasks could not be loaded.</Alert>
          )}
          {!approvedTasksFailed && !loading && approvedTasksByMonth.length === 0 && (
            <Alert severity="info">No supervisor approved tasks recorded yet.</Alert>
          )}
          {approvedTasksByMonth.length > 0 && (
            <Stack spacing={1}>
              {approvedTasksByMonth.map(([month, tasks], monthIndex) => (
                <Accordion key={month} defaultExpanded={monthIndex === 0} disableGutters variant="outlined" sx={{ borderRadius: 1, '&:before': { display: 'none' } }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 48, '& .MuiAccordionSummary-content': { my: 0.75 } }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%', pr: 1 }}>
                      <Typography fontWeight={900}>{month}</Typography>
                      <Chip size="small" label={`${tasks.length} approved task${tasks.length === 1 ? '' : 's'}`} />
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    <Stack spacing={1}>
                      {tasks.map((task) => {
                        const materials = approvedTaskMaterials[task.id] ?? [];
                        return (
                          <Accordion key={task.id} disableGutters variant="outlined" sx={{ borderRadius: 1, '&:before': { display: 'none' } }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={0.75}
                                alignItems={{ xs: 'flex-start', sm: 'center' }}
                                justifyContent="space-between"
                                sx={{ width: '100%', pr: 1 }}
                              >
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography fontWeight={900} noWrap>{task.taskCode ?? task.id}</Typography>
                                  <Typography variant="caption" color="text.secondary" noWrap>{task.title}</Typography>
                                </Box>
                                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                                  <Chip size="small" color="success" label="Approved" />
                                  <Typography variant="caption" color="text.secondary">{readableDate(approvalDate(task))}</Typography>
                                </Stack>
                              </Stack>
                            </AccordionSummary>
                            <AccordionDetails sx={{ pt: 0 }}>
                              <Grid container spacing={1}>
                                <Grid item xs={6} sm={3}>
                                  <Typography variant="caption" color="text.secondary">Percent complete</Typography>
                                  <Typography variant="body2" fontWeight={800}>{percent(task.percentComplete ?? 0)}</Typography>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                  <Typography variant="caption" color="text.secondary">Payment status</Typography>
                                  <Typography variant="body2" fontWeight={800}>{packetStatusLabel(task.paymentStatus ?? 'not eligible')}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <Typography variant="caption" color="text.secondary">Crew</Typography>
                                  <Typography variant="body2">{task.teamAssignments?.map((row) => row.team?.name).filter(Boolean).join(', ') || '-'}</Typography>
                                </Grid>
                              </Grid>
                              <Typography fontWeight={900} sx={{ mt: 1, mb: 0.5 }}>Material listing</Typography>
                              {materials.length === 0 ? (
                                <Alert severity="info">No materials are linked to this approved task.</Alert>
                              ) : (
                                <Table size="small" sx={{ '& td, & th': { py: 0.45, px: 0.75, fontSize: 12 } }}>
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Material</TableCell>
                                      <TableCell>Status</TableCell>
                                      <TableCell align="right">Required</TableCell>
                                      <TableCell align="right">Issued</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {materials.map((material) => (
                                      <TableRow key={material.id}>
                                        <TableCell>{materialLabel(material)}</TableCell>
                                        <TableCell><Chip size="small" label={packetStatusLabel(material.allocationStatus)} /></TableCell>
                                        <TableCell align="right">{materialQuantity(material)}</TableCell>
                                        <TableCell align="right">{Number(material.quantityAllocated || 0)} {material.resource?.unitOfMeasure || ''}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              )}
                            </AccordionDetails>
                          </Accordion>
                        );
                      })}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Stack>
          )}
          <Typography fontWeight={900} sx={{ mt: 1.5, mb: 0.5 }}>Materials received from purchase orders</Typography>
          {receivedPurchaseOrderMaterials.length === 0 ? (
            <Alert severity="info">No purchase-order material receipts are linked to QS claims yet.</Alert>
          ) : (
            <Table size="small" sx={{ '& td, & th': { py: 0.45, px: 0.75, fontSize: 12 } }}>
              <TableHead>
                <TableRow>
                  <TableCell>Material</TableCell>
                  <TableCell>BQ line</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Claim value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {receivedPurchaseOrderMaterials.map((material) => (
                  <TableRow key={material.id}>
                    <TableCell>{material.description ?? 'Material received'}</TableCell>
                    <TableCell>{material.bqCode} - {material.bqDescription}</TableCell>
                    <TableCell><Chip size="small" label={packetStatusLabel(material.status)} /></TableCell>
                    <TableCell align="right">{money(material.claimableValue)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      </Stack>
    );
  }

  return (
    <Stack spacing={1.5}>
      <Typography variant="h4" fontWeight={900}>QS</Typography>
      {statusAlerts}
      <Grid container spacing={1}>
        <Grid item xs={3}>{metric('Completed BQ items', String(completedItems), 'fully claimed progress')}</Grid>
        <Grid item xs={3}>{metric('Partially complete', String(partialItems), 'BQ items')}</Grid>
        <Grid item xs={3}>{metric('Not started', String(notStartedItems), 'BQ items')}</Grid>
        <Grid item xs={3}>{metric('Drawdown due', money(claimableValue), 'claimable this certificate')}</Grid>
      </Grid>

      <Box>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="caption" color="text.secondary">Drawdown tracker</Typography>
          <Typography variant="caption" fontWeight={800}>{drawdownProgress}% of maximum claimable</Typography>
        </Stack>
        <LinearProgress variant="determinate" value={drawdownProgress} sx={{ height: 8, borderRadius: 1, mt: 0.5 }} />
      </Box>

      <Typography fontWeight={900}>Current Drawdown Request</Typography>
      <Button
        component={RouterLink}
        to="/field/qs/drawdown"
        fullWidth
        variant="outlined"
        sx={{ minHeight: 78, borderRadius: 1, justifyContent: 'space-between', px: 2, color: 'text.primary', borderColor: 'divider' }}
      >
        <Box sx={{ textAlign: 'left' }}>
          <Typography fontWeight={900}>Current Drawdown Request</Typography>
          <Typography variant="caption" color="text.secondary">
            {lines.length} claimable BQ {lines.length === 1 ? 'line' : 'lines'}
          </Typography>
        </Box>
        <Typography fontWeight={900}>{money(claimableValue)}</Typography>
      </Button>

      <Typography fontWeight={900}>Previous Drawdown Requests</Typography>
      <Button
        component={RouterLink}
        to="/field/qs/previous-drawdowns"
        fullWidth
        variant="outlined"
        sx={{ minHeight: 64, borderRadius: 1, justifyContent: 'space-between', px: 2, color: 'text.primary', borderColor: 'divider' }}
      >
        <Box sx={{ textAlign: 'left' }}>
          <Typography fontWeight={900}>Previously Approved Drawdown Requests</Typography>
          <Typography variant="caption" color="text.secondary">
            {approvedDrawdownPackets.length} approved or paid drawdown {approvedDrawdownPackets.length === 1 ? 'request' : 'requests'}
          </Typography>
        </Box>
        <Typography fontWeight={900}>Open</Typography>
      </Button>
      {view === 'previous-drawdowns' && (
        <Paper sx={{ p: 1, borderRadius: 1 }}>
          {packetsFailed && <Alert severity="warning">Previous approved drawdown requests could not be loaded.</Alert>}
          {!packetsFailed && !loading && approvedDrawdownsByMonth.length === 0 && (
            <Alert severity="info">No approved drawdown requests have been recorded yet.</Alert>
          )}
          <Stack spacing={1}>
            {approvedDrawdownsByMonth.map(([month, monthPackets], monthIndex) => (
              <Accordion key={month} defaultExpanded={monthIndex === 0} disableGutters variant="outlined" sx={{ borderRadius: 1, '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%', pr: 1 }}>
                    <Typography fontWeight={900}>{month}</Typography>
                    <Chip size="small" label={`${monthPackets.length} request${monthPackets.length === 1 ? '' : 's'}`} />
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={1}>
                    {monthPackets.map((packet) => (
                      <Accordion key={packet.id} disableGutters variant="outlined" sx={{ borderRadius: 1, '&:before': { display: 'none' } }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 42, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                          <Stack direction="row" justifyContent="space-between" sx={{ width: '100%', pr: 1 }}>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography fontWeight={900} noWrap>{packet.drawdownReference}</Typography>
                              <Typography variant="caption" color="text.secondary">{readableDate(packet.approvedAt ?? packet.paidAt ?? packet.updatedAt ?? packet.createdAt)}</Typography>
                            </Box>
                            <Typography variant="body2" fontWeight={900}>{money(packet.netAmount)}</Typography>
                          </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Grid container spacing={1}>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Snapshot date</Typography>
                              <Typography variant="body2">{readableDate(packet.snapshotGeneratedAt)}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Status</Typography>
                              <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{packetStatusLabel(packet.status)}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Approved date</Typography>
                              <Typography variant="body2">{readableDate(packet.approvedAt ?? packet.paidAt)}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Items</Typography>
                              <Typography variant="body2">{packet.itemCount}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Gross</Typography>
                              <Typography variant="body2">{money(packet.grossAmount)}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Retention</Typography>
                              <Typography variant="body2">{money(packet.retentionAmount)}</Typography>
                            </Grid>
                          </Grid>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        </Paper>
      )}
      {view !== 'previous-drawdowns' && packetsFailed && <Alert severity="warning">Previous drawdown requests could not be loaded.</Alert>}
      {view !== 'previous-drawdowns' && !packetsFailed && !loading && packets.length === 0 && (
        <Alert severity="info">No drawdown packets have been generated for this project yet.</Alert>
      )}
      {view !== 'previous-drawdowns' && packets.length > 0 && (
        <Paper sx={{ p: 1, borderRadius: 1 }}>
          {packets.map((packet) => (
            <Accordion key={packet.id} disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 42, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                <Stack direction="row" justifyContent="space-between" sx={{ width: '100%' }}>
                  <Typography fontWeight={900}>{packet.drawdownReference}</Typography>
                  <Typography variant="caption">{money(packet.netAmount)}</Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Snapshot date</Typography>
                    <Typography variant="body2">{packet.snapshotGeneratedAt?.slice(0, 10) ?? '—'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Status</Typography>
                    <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{packetStatusLabel(packet.status)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Gross</Typography>
                    <Typography variant="body2">{money(packet.grossAmount)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Retention</Typography>
                    <Typography variant="body2">{money(packet.retentionAmount)}</Typography>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          ))}
        </Paper>
      )}
    </Stack>
  );
}
